// =====================================================
// BATCH IMPORT DIALOG
// Stage 2: Participants & Pricing
// Dialog for batch importing participants from CSV
// =====================================================

import { useState, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { parseCSV, type ParsedCSVData } from '../utils/csvParser';
import { validateImportRow, type ImportValidationError } from '../utils/importValidation';
import { eventParticipantsRepository } from '@/data/eventParticipantsRepository';
import * as eventGuestsRepo from '@/data/eventGuestsRepository';
import { useToast } from '@/hooks/use-toast';

// ===================
// TYPES
// ===================

interface BatchImportDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';

const HEADER_ALIASES: Record<'minister_adb_code' | 'guest_name' | 'guest_dni' | 'guest_email' | 'guest_phone' | 'is_mandatory', string[]> = {
  minister_adb_code: ['codigo_adb', 'codigo', 'cod_adb', 'adb', 'adb_code', 'registro_adb', 'codigo_ministro'],
  guest_name: ['nombre_invitado', 'invitado', 'nombre', 'nombre_completo', 'guest', 'guest_name'],
  guest_dni: ['dni_invitado', 'dni', 'ci', 'nro_ci', 'carnet', 'cedula', 'cedula_identidad'],
  guest_email: ['email_invitado', 'email', 'correo', 'e_mail', 'correo_electronico'],
  guest_phone: ['telefono_invitado', 'telefono', 'celular', 'cel', 'phone'],
  is_mandatory: ['obligatorio', 'mandatory'],
};

interface ColumnMapping {
  csvColumn: string;
  targetField: 'minister_adb_code' | 'guest_name' | 'guest_dni' | 'guest_email' | 'guest_phone' | 'is_mandatory' | null;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
}

// ===================
// COMPONENT
// ===================

export function BatchImportDialog({ 
  eventId, 
  open, 
  onOpenChange,
  onImportComplete,
}: BatchImportDialogProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedData, setParsedData] = useState<ParsedCSVData | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [validationErrors, setValidationErrors] = useState<ImportValidationError[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep('upload');
      setParsedData(null);
      setColumnMappings([]);
      setValidationErrors([]);
      setImportProgress(0);
      setImportResult(null);
    }
    onOpenChange(newOpen);
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Formato inválido',
        description: 'Por favor selecciona un archivo CSV',
        variant: 'destructive',
      });
      return;
    }

    try {
      const parsed = await parseCSV(file);
      setParsedData(parsed);
      
      // Create initial column mappings
      const initialMappings: ColumnMapping[] = parsed.headers.map((header: string) => ({
        csvColumn: header,
        targetField: autoMapColumn(header),
      }));
      setColumnMappings(initialMappings);
      
      setStep('mapping');
    } catch (error) {
      toast({
        title: 'Error al leer archivo',
        description: error instanceof Error ? error.message : 'No se pudo leer el archivo CSV',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Auto-map CSV columns by exact (normalized) header aliases. Substring matching
  // mis-mapped headers like "ciudad"/"provincia" (contain "ci") and "nombre".
  const autoMapColumn = (header: string): ColumnMapping['targetField'] => {
    const normalized = header
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/[\s\-.]+/g, '_');
    const match = (Object.entries(HEADER_ALIASES) as [NonNullable<ColumnMapping['targetField']>, string[]][])
      .find(([, aliases]) => aliases.includes(normalized));
    return match ? match[0] : null;
  };

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle column mapping change
  const handleMappingChange = (index: number, targetField: ColumnMapping['targetField']) => {
    const newMappings = [...columnMappings];
    newMappings[index].targetField = targetField;
    setColumnMappings(newMappings);
  };

  // Validate and preview data
  const handlePreview = () => {
    if (!parsedData) return;

    const errors: ImportValidationError[] = [];
    
    parsedData.rows.forEach((row: Record<string, string>, index: number) => {
      const mappedRow: Record<string, string> = {};
      columnMappings.forEach(mapping => {
        if (mapping.targetField) {
          mappedRow[mapping.targetField] = row[mapping.csvColumn];
        }
      });

      const rowErrors = validateImportRow(mappedRow, index + 1);
      errors.push(...rowErrors);
    });

    setValidationErrors(errors);
    setStep('preview');
  };

  // Execute import
  const handleImport = async () => {
    if (!parsedData || isImporting) return;

    setStep('importing');
    setImportProgress(0);
    setIsImporting(true);

    const result: ImportResult = {
      success: 0,
      failed: 0,
      errors: [],
    };

    const BATCH_SIZE = 10; // Process 10 rows at a time for better UX feedback
    const totalRows = parsedData.rows.length;
    const createdIds: string[] = [];

    try {
      const codeColumn = columnMappings.find((m) => m.targetField === 'minister_adb_code')?.csvColumn;
      const ministersByCode = await eventParticipantsRepository.findMinistersByCodes(
        codeColumn ? parsedData.rows.map((r: Record<string, string>) => r[codeColumn] ?? '') : [],
      );

      for (let i = 0; i < totalRows; i += BATCH_SIZE) {
        const batchEnd = Math.min(i + BATCH_SIZE, totalRows);
        const batch = parsedData.rows.slice(i, batchEnd);

        // Process batch in parallel
        const batchResults = await Promise.allSettled(
          batch.map(async (row: Record<string, string>, batchIndex: number) => {
            const actualIndex = i + batchIndex;
            const mappedRow: Record<string, string> = {};
            
            columnMappings.forEach(mapping => {
              if (mapping.targetField) {
                mappedRow[mapping.targetField] = row[mapping.csvColumn];
              }
            });

            // Determine if this is a minister or guest
            const isMinister = !!mappedRow.minister_adb_code;
            const isMandatory = ['true', '1', 'si', 'sí', 'x'].includes((mappedRow.is_mandatory ?? '').trim().toLowerCase());
            
            if (isMinister) {
              // Register minister by ADB code (preloaded above)
              const minister = ministersByCode.get(mappedRow.minister_adb_code.trim().toUpperCase());
              if (!minister) {
                throw new Error(`Ministro no encontrado: ${mappedRow.minister_adb_code}`);
              }
              
              const created = await eventParticipantsRepository.registerParticipant({
                event_id: eventId,
                minister_id: minister.id,
                is_mandatory: isMandatory,
                // Priced in one batch after the import (deterministic participant counts)
                calculate_price: false,
              });
              createdIds.push(created.id);
            } else {
              // Reuse guest by CI (dedupe), then register
              const guest = await eventGuestsRepo.findOrCreateEventGuest({
                full_name: mappedRow.guest_name,
                dni: mappedRow.guest_dni || undefined,
                email: mappedRow.guest_email || undefined,
                phone: mappedRow.guest_phone || undefined,
              });

              const created = await eventParticipantsRepository.registerParticipant({
                event_id: eventId,
                guest_id: guest.id,
                is_mandatory: isMandatory,
                calculate_price: false,
              });
              createdIds.push(created.id);
            }
            
            return { success: true, row: actualIndex };
          })
        );

        // Process batch results
        batchResults.forEach((promiseResult: PromiseSettledResult<{success: boolean; row: number}>, batchIndex: number) => {
          const rowNumber = i + batchIndex + 2; // +2 for header row and 1-indexed
          
          if (promiseResult.status === 'fulfilled') {
            result.success++;
          } else {
            result.failed++;
            result.errors.push({
              row: rowNumber,
              message: promiseResult.reason instanceof Error 
                ? promiseResult.reason.message 
                : 'Error desconocido',
            });
          }
        });

        // Update progress after each batch
        setImportProgress(Math.round((batchEnd / totalRows) * 100));
      }

      // Apply pricing rules to all imported rows at once
      if (createdIds.length > 0) {
        await eventParticipantsRepository.recalculateParticipantPrices(eventId, createdIds);
      }

      setImportResult(result);
      setStep('complete');
      
      if (result.success > 0) {
        onImportComplete();
        toast({
          title: 'Importación completada',
          description: `${result.success} participantes importados exitosamente`,
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Error en importación',
        description: 'Ocurrió un error durante la importación',
        variant: 'destructive',
      });
      setStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  // Download error report
  const handleDownloadErrors = () => {
    if (!importResult) return;

    const csvContent = [
      ['Fila', 'Error'].join(','),
      ...importResult.errors.map(e => [e.row, `"${e.message}"`].join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `errores-importacion-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const template = [
      ['codigo_adb', 'nombre_invitado', 'dni_invitado', 'email_invitado', 'telefono_invitado', 'obligatorio'].join(','),
      ['SOL26-001', '', '', '', '', 'false'].join(','),
      ['', 'Juan Pérez', '12345678', 'juan@ejemplo.com', '70123456', 'true'].join(','),
    ].join('\n');

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla-participantes.csv';
    link.click();

    toast({
      title: 'Plantilla descargada',
      description: 'Usa este archivo como ejemplo para tu importación',
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importación masiva de participantes</DialogTitle>
          <DialogDescription>
            Importa múltiples participantes desde un archivo CSV
          </DialogDescription>
        </DialogHeader>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
            >
              <Upload className="mx-auto mb-4 size-12 text-muted-foreground" />
              <p className="mb-2 text-sm font-medium">
                Arrastra tu archivo CSV aquí o haz clic para seleccionar
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                Máximo 1000 filas • Solo archivos .csv
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
                id="csv-input"
              />
              <label htmlFor="csv-input">
                <Button variant="outline" size="sm" asChild>
                  <span>Seleccionar archivo</span>
                </Button>
              </label>
            </div>

            <Alert>
              <FileText className="size-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium">Formato del archivo CSV:</p>
                  <ul className="list-inside list-disc space-y-1 text-sm">
                    <li>Para ministros: incluye columna con código ADB</li>
                    <li>Para invitados: incluye nombre, DNI, email, teléfono</li>
                    <li>Columna opcional: obligatorio (true/false)</li>
                  </ul>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="h-auto p-0"
                  >
                    <Download className="mr-2 size-3" />
                    Descargar plantilla de ejemplo
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Step: Column Mapping */}
        {step === 'mapping' && parsedData && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="size-4" />
              <AlertDescription>
                Mapea las columnas de tu CSV a los campos del sistema. {parsedData.rows.length} filas detectadas.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              {columnMappings.map((mapping, index) => (
                <div key={index} className="grid grid-cols-2 items-center gap-4">
                  <div>
                    <Label className="text-sm font-medium">{mapping.csvColumn}</Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Ejemplo: {parsedData.rows[0]?.[mapping.csvColumn] || '(vacío)'}
                    </p>
                  </div>
                  <Select
                    value={mapping.targetField || 'none'}
                    onValueChange={(value) => 
                      handleMappingChange(index, value === 'none' ? null : value as ColumnMapping['targetField'])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona campo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">No importar</span>
                      </SelectItem>
                      <SelectItem value="minister_adb_code">Código ADB (Ministro)</SelectItem>
                      <SelectItem value="guest_name">Nombre (Invitado)</SelectItem>
                      <SelectItem value="guest_dni">DNI (Invitado)</SelectItem>
                      <SelectItem value="guest_email">Email (Invitado)</SelectItem>
                      <SelectItem value="guest_phone">Teléfono (Invitado)</SelectItem>
                      <SelectItem value="is_mandatory">Obligatorio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('upload')}>
                Volver
              </Button>
              <Button onClick={handlePreview}>
                Continuar a Vista Previa
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && parsedData && (
          <div className="space-y-4">
            {validationErrors.length > 0 ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertDescription>
                  Se encontraron {validationErrors.length} errores de validación. 
                  Por favor corrige el archivo y vuelve a intentar.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <CheckCircle2 className="size-4 text-green-600" />
                <AlertDescription>
                  Datos validados correctamente. {parsedData.rows.length} participantes listos para importar.
                </AlertDescription>
              </Alert>
            )}

            {validationErrors.length > 0 && (
              <div className="max-h-60 overflow-y-auto rounded-lg border">
                <div className="space-y-2 p-4">
                  {validationErrors.slice(0, 20).map((error, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium">Fila {error.row}:</span>{' '}
                      <span className="text-destructive">{error.message}</span>
                    </div>
                  ))}
                  {validationErrors.length > 20 && (
                    <p className="text-sm text-muted-foreground">
                      ...y {validationErrors.length - 20} errores más
                    </p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('mapping')}>
                Volver
              </Button>
              <Button 
                onClick={handleImport}
                disabled={validationErrors.length > 0}
              >
                Importar {parsedData.rows.length} Participantes
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <p className="mb-4 text-sm font-medium">Importando participantes...</p>
              <Progress value={importProgress} className="w-full" />
              <p className="mt-2 text-xs text-muted-foreground">{importProgress}% completado</p>
            </div>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && importResult && (
          <div className="space-y-4">
            <Alert variant={importResult.failed > 0 ? 'default' : 'default'}>
              <CheckCircle2 className="size-4" />
              <AlertDescription>
                <p className="font-medium">Importación completada</p>
                <div className="mt-2 space-y-1 text-sm">
                  <p>✅ {importResult.success} participantes importados correctamente</p>
                  {importResult.failed > 0 && (
                    <p className="text-destructive">❌ {importResult.failed} fallidos</p>
                  )}
                </div>
              </AlertDescription>
            </Alert>

            {importResult.errors.length > 0 && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium">Errores de importación:</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadErrors}
                  >
                    <Download className="mr-2 size-4" />
                    Descargar reporte
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto rounded-lg border">
                  <div className="space-y-2 p-4">
                    {importResult.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium">Fila {error.row}:</span>{' '}
                        <span className="text-destructive">{error.message}</span>
                      </div>
                    ))}
                    {importResult.errors.length > 10 && (
                      <p className="text-sm text-muted-foreground">
                        ...y {importResult.errors.length - 10} errores más
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
